import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Zap, Shield, TrendingUp, Users, CheckCircle2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";


const HomePage = () => {


  const features = [
    { icon: Zap, title: 'Earn Instantly', description: 'Receive payments directly through Recit credits, redeemable anytime.' },
    { icon: Globe, title: 'Global Visibility', description: 'Reach a worldwide audience of creative professionals and brands.' },
    { icon: TrendingUp, title: 'Grow Your Portfolio', description: 'Access insights and analytics to improve your performance.' },
    { icon: Users, title: 'Collaborate Easily', description: 'Partner with brands and other creators for campaigns and projects.' }
  ];

  const categories = [
    { title: 'Design Layouts', description: 'Templates, graphics, UI/UX kits, and branding assets.' },
    { title: 'Automation Workflows', description: 'Time-saving blueprints for design, marketing, and development.' },
    { title: 'AI Tools & Prompts', description: 'Smart scripts and models that enhance creative efficiency.' },
    { title: 'Video & Animation Assets', description: 'Pre-built motion templates and transitions.' }
  ];

  const differentiators = [
    'Creator-First Economy: Empowering creators with fair earnings and visibility.',
    'Curated Quality: Every asset is reviewed to meet professional standards.',
    'AI-Driven Search: Discover assets through intelligent recommendations.',
    'Seamless Transactions: Buy and sell securely using Recit Access Credits.',
    'Sustainable Growth: From freelancer to full-scale studio, Recit grows with you.'
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "Digital Designer",
      quote: "Recit transformed how I monetize my work. The global reach is incredible!"
    },
    {
      name: "Michael Torres",
      role: "Creative Director",
      quote: "Best marketplace for high-quality assets. Saves us countless hours."
    },
    {
      name: "Priya Sharma",
      role: "Freelance Developer",
      quote: "From upload to earnings in minutes. The platform is seamless and intuitive."
    },
    {
      name: "Daniel Cooper",
      role: "Brand Strategist",
      quote: "The quality of templates and workflows here is unmatched. Highly recommended!"
    },
    {
      name: "Amina Yusuf",
      role: "UI/UX Designer",
      quote: "Recit helped me scale my freelance income faster than any other platform."
    },
    {
      name: "Leo Martínez",
      role: "AI Automation Specialist",
      quote: "Amazing place to share AI tools and prompts — buyers love the micro-products!"
    },
    {
      name: "Sarah Chen",
      role: "Digital Designer",
      quote: "Recit transformed how I monetize my work. The global reach is incredible!"
    },
    {
      name: "Michael Torres",
      role: "Creative Director",
      quote: "Best marketplace for high-quality assets. Saves us countless hours."
    }
  ];
  const testimonialSettings = {
    dots: true,
    infinite: true,
    speed: 600,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 2000,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  };


  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 flex items-center justify-center bg-section-bg rounded-2xl max-w-[1138px] mx-auto my-16 shadow-lg">
        <div className="max-w-[1138px] mx-auto px-6 text-center">
          <div className="max-w-4xl mx-auto space-y-8">
            <h1 className="text-sm sm:text-2xl md:text-3xl lg:text-[36px]  font-bold text-text-primary leading-tight animate-fade-in">
              Welcome to Recit, <span className="text-primary">Where Creativity is Currency</span>
            </h1>

            <p className="text-xl md:text-2xl text-text-secondary mx-auto max-w-[370px] md:max-w-[550px] lg:max-w-[750px]  leading-relaxed ">
              At Recit, creativity never sits idle — it earns, inspires, and grows.
              <br />A global ecosystem where creators, designers, developers, and innovators
              exchange value, ideas, and opportunity.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 rounded-lg"
                onClick={() => window.location.href = 'https://go.recit.sg/signup/customer'}
              >
                Register Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-8 py-6 text-lg font-semibold transition-all duration-300 rounded-lg"
                onClick={() => window.location.href = 'https://go.recit.sg'}
              >
                Explore Marketplace
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Turn Creativity Into a Career */}
      <section className=" pb-16">
        <div className="max-w-[1138px] mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6  ">
                Turn Creativity<br />
                <span className="block mt-2">Into a Career</span>
              </h2>

              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Your ideas deserve a global stage — and Recit gives you one. Upload, showcase, and sell your creative assets in minutes using tools built to help you thrive as a digital entrepreneur.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card key={index} className="border border-border shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-card-bg rounded-lg">
                    <CardContent className="p-8">
                      <div className="w-14 h-14 bg-secondary/10 rounded-xl flex items-center justify-center mb-6">
                        <Icon className="h-7 w-7 text-primary" />
                      </div>
                      <h3 className="text-xl font-bold text-text-primary mb-3">{feature.title}</h3>
                      <p className="text-text-secondary leading-relaxed">{feature.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg"
                onClick={() => window.location.href = 'https://go.recit.sg/signup/customer'}
              >
                Start Selling on Recit
                <Sparkles className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Discover. Download. Do More. */}
      <section className=" pb-16 text-text-primary">
        <div className="max-w-[1138px] mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            {/* Heading + paragraph */}
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                Discover. Download. Do More.
              </h2>
              <p className="text-xl md:text-2xl lg:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Explore thousands of verified digital products — built by top creators, tested for quality, and ready to integrate.
              </p>
            </div>

            {/* Categories */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {categories.map((category, index) => (
                <div
                  key={index}
                  className="bg-card-bg rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-border hover:border-primary"
                >
                  <h3 className="text-2xl font-bold mb-3 text-primary">{category.title}</h3>
                  <p className="text-text-secondary leading-relaxed">{category.description}</p>
                </div>
              ))}
            </div>

            {/* Button */}
            <div className="text-center mt-6">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white px-8 py-6 text-lg md:text-xl font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 rounded-lg"
                onClick={() => window.location.href = 'https://go.recit.sg/s'}
              >
                Explore the Marketplace
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>


      {/* The Recit Difference */}
      <section className="py-16 bg-section-bg rounded-2xl max-w-[1000px] mx-auto mb-16 shadow-lg">
        <div className="max-w-[1138px] mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-6xl lg:text-7xl  font-bold text-slate-900 mb-6">The Recit Difference</h2>
              <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto">
                Recit is more than a marketplace — it's a creative movement built on transparency, collaboration, and empowerment.
              </p>
            </div>

            <div className="space-y-6">
              {differentiators.map((item, index) => (
                <div key={index} className="flex items-start gap-4 bg-card-bg p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-border">
                  <CheckCircle2 className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
                  <p className="text-lg text-text-primary leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Built for a Global Creative Generation */}
      <section className="py-16 bg-section-bg rounded-2xl max-w-[1000px] mx-auto mb-16 shadow-lg">
        <div className="max-w-[1138px] mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Built for a Global Creative Generation</h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              Recit unites creators and users from across the globe — from designers in Singapore to developers in Dubai, marketers in the UK, and educators in the US. We're creating a borderless ecosystem where talent and opportunity meet without limits.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {/* Testimonials */}
      <section className="pb-16 text-text-primary">
        <div className="max-w-[1138px] mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-16">
              Creators Love Recit
            </h2>

            <Slider {...testimonialSettings}>
              {testimonials.map((testimonial, index) => (
                <div key={index} className="px-4">
                  <Card className="bg-card-bg border-border hover:border-primary transition-all duration-300 h-full">
                    <CardContent className="p-8">
                      <p className="text-text-secondary mb-6 italic leading-relaxed">
                        "{testimonial.quote}"
                      </p>
                      <div>
                        <p className="font-bold text-text-primary">{testimonial.name}</p>
                        <p className="text-sm text-primary">{testimonial.role}</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </Slider>

          </div>
        </div>
      </section>




      {/* Join the Recit Revolution */}
      <section className="pb-16">
        <div className="max-w-[1138px] mx-auto px-6">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">Join the Recit Revolution</h2>
            <p className="text-xl text-gray-600 mb-12">
              Every template, tool, and workflow shared or used helps build a future where creativity fuels the digital economy.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              <Card className="border border-border hover:border-primary transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold text-text-primary mb-4">I'm a Creator</h3>
                  <p className="text-text-secondary mb-6">Upload and sell your assets</p>
                  <Button
                    className="w-full bg-primary hover:bg-primary/90 text-white rounded-lg"
                    onClick={() => window.location.href = 'https://go.recit.sg/signup/customer'}
                  >
                    Get Started
                  </Button>
                </CardContent>
              </Card>

              <Card className="border border-border hover:border-primary transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold text-text-primary mb-4">I'm a User</h3>
                  <p className="text-text-secondary mb-6">Explore the marketplace</p>
                  <Button
                    className="w-full bg-secondary hover:bg-secondary/90 text-white rounded-lg"
                    onClick={() => window.location.href = 'https://go.recit.sg/s'}
                  >
                    Browse Now
                  </Button>
                </CardContent>
              </Card>

              <Card className="border border-border hover:border-primary transition-all duration-300 hover:shadow-xl">
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-bold text-text-primary mb-4">Join Community</h3>
                  <p className="text-text-secondary mb-6">Collaborate, connect, and grow</p>
                  <Button
                    className="w-full bg-accent hover:bg-accent/90 text-white rounded-lg"
                    onClick={() => window.location.href = 'https://go.recit.sg'}
                  >
                    Connect Now
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-section-bg rounded-2xl max-w-[1000px] mx-auto  mb-16 shadow-lg">
        <div className="max-w-[1138px] mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-5xl md:text-6xl font-bold text-text-primary mb-6">The Marketplace Awaits</h2>
            <p className="text-xl text-text-secondary mb-12 leading-relaxed">
              Step into a world where ideas meet execution — and every click takes you closer to something extraordinary. From templates that sell to workflows that scale, Recit gives you the creative edge you've been looking for.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white px-10 py-7 text-xl font-bold shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-105 rounded-lg"
                onClick={() => window.location.href = 'https://go.recit.sg'}
              >
                Visit the Marketplace
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary hover:text-white px-10 py-7 text-xl font-bold transition-all duration-300 rounded-lg"
                onClick={() => window.location.href = 'https://go.recit.sg/signup/customer'}
              >
                Sign Up for Free
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;